import React, {useState} from 'react';
import {Box, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow} from '@mui/material';
import NetworkDashboardShell from '../components/NetworkDashboard.comp';
import CircularLoader from '../../../components/CircularLoader';
import NoDataAvailable from '../../../components/NoDataAvailable';
import TakeActionFlow from '../../DQPro/PerformanceSummary/TakeActionFlow.comp';
import {useDashboard} from '../../../context/DashboardContext';
import {findCard, rowsFrom, rowMatches, cellText} from '../networkData';
import {isScopeSuppressed} from '../../DashboardCommon/dashboardCards';
import {NAVY, TEAL, PageHeader, headCell, bodyCell} from '../components/widgets.comp';
import {EvidenceCell, RecommendationCell} from '../../DashboardCommon/RecommendationCells.comp';
import {useTableControls} from '../../DashboardCommon/useTableControls';
import {SortFilterHeaderCell} from '../../DashboardCommon/SortFilterHeaderCell.comp';
import {TableToolbar} from '../../DashboardCommon/TableToolbar.comp';

/* Slide 33 — Recommended Learning Programs. The Country/Region/School rows
   (A-G) and blank Baseline Evidence / Recommended Learning columns are
   copied verbatim from the deck's own table — no recommendation engine
   exists yet, so values are left as em dashes rather than invented, same
   convention as School Dashboard's identical page. "Take Action" is the
   only real button on this slide (shape fill 188038) — "Evidence &
   Recommendation logic is stored in a Google Doc." and "Full program list
   targeted at 'whole-school' level..." are yellow annotation boxes
   (instructions for the build), not literal on-page copy, so neither is
   rendered. Table styling matches this dashboard's own headCell/bodyCell
   convention, not the mockup's own colors. */
/* Live rows: SND-33-02 → SND.S_14.C_2 (PriorityTableCard), in this page's own
   layout. The card sends Country, Region ("—" for a network) and School.
   Every column sorts and filters from its header (QA 9 Oct 2026 — the slide's
   Country/Region/School/Program drop-down bar is gone; a program is found
   through the search box, which matches any cell). */
const COLUMNS = [
    {key: 'country', label: 'Country', type: 'text'},
    {key: 'region', label: 'Region', type: 'text'},
    {key: 'school', label: 'School', type: 'text'},
    // The slide renames the card's Baseline Evidence column (Ian, 1 Oct). Both list cells sort by their text; no checklist.
    {key: 'evidence', label: 'Areas of Weakness', type: 'text', filterable: false, accessor: (r) => cellText(r._raw?.evidence)},
    {key: 'recommendation', label: 'Recommended Learning', type: 'text', filterable: false, accessor: (r) => cellText(r._raw?.recommendation)},
];

const RecommendationTable = ({rows, emptyText = 'No recommendations yet.', query, onQuery}) => {
    const t = useTableControls(rows, COLUMNS);
    return (
        <>
            <TableToolbar {...t} query={query} onQuery={onQuery} placeholder="Search by country, region, school…"/>
            <TableContainer sx={{
                border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', overflow: 'hidden',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            {COLUMNS.map((c, i) => (
                                <SortFilterHeaderCell key={c.key} {...t.headerProps(c.key)} sx={{...headCell, textAlign: 'left', ...(i === 0 ? {pl: 2.5} : {})}}/>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {t.rows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={5} sx={{...bodyCell, textAlign: 'center', py: 4, color: '#6A7282'}}>{emptyText}</TableCell>
                            </TableRow>
                        )}
                        {t.rows.map((row, i) => (
                            <TableRow key={`${row.country}-${row.region}-${row.school}-${rows.indexOf(row)}`} sx={{backgroundColor: i % 2 === 0 ? 'var(--brand-surface, #ffffff)' : 'var(--brand-surface-subtle, #F9FAFB)'}}>
                                <TableCell sx={{...bodyCell, textAlign: 'left', fontWeight: 700, color: NAVY, pl: 2.5}}>{row.country}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'left'}}>{row.region}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'left'}}>{row.school}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'left'}}><EvidenceCell value={row._raw?.evidence} fallback={row.evidence}/></TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'left'}}><RecommendationCell row={row}/></TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );
};

// Rows for units under the n floor are not built upstream; a dashboard whose own population is under it says so.
const SUPPRESSED_EMPTY = 'Priority actions are not shown for groups of fewer than 10 students.';
const NO_MATCH = 'No row matches the search and filters.';

const PRIORITY_ACTIONS_LP_SECTION_CODE = 'S_14';
const CARD_SECTIONS = [PRIORITY_ACTIONS_LP_SECTION_CODE];

const PriorityActionsLearningProgramPage = () => {
    const {dashboardData, loading} = useDashboard({ card_sections: CARD_SECTIONS });
    const card = findCard(dashboardData, PRIORITY_ACTIONS_LP_SECTION_CODE, 'C_2');
    const [query, setQuery] = useState('');
    const rows = rowsFrom(card, {
        country: 'Country', region: 'Region', school: 'School',
        evidence: 'Baseline Evidence', recommendation: 'Recommended Learning',
    });
    const q = query.trim().toLowerCase();
    const shown = rows.filter((r) => rowMatches(r, q));

    if (loading) {
        return (
            <NetworkDashboardShell>
                <CircularLoader />
            </NetworkDashboardShell>
        );
    }

    if (!dashboardData) {
        return (
            <NetworkDashboardShell>
                <NoDataAvailable />
            </NetworkDashboardShell>
        );
    }

    return (
    <NetworkDashboardShell>
        <PageHeader
            title="Recommended Learning Programs"
            subtitle="The crosswalk converts priority COS areas into targeted learning programs."
        />

        {card?.take_action !== false && rows.length > 0 && (
        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mb: 2.5}}>
            {/* Opens the SW/SNW-P1A or -P1B Performance Summary Take Action flow (see TakeActionFlow). */}
            <TakeActionFlow>
                {(openTakeAction) => (
                    <Button onClick={openTakeAction} sx={{textTransform: 'none', fontWeight: 700, backgroundColor: TEAL, color: '#fff', '&:hover': {backgroundColor: TEAL}}}>
                        Take Action
                    </Button>
                )}
            </TakeActionFlow>
        </Box>
        )}

        <RecommendationTable
            rows={shown}
            query={query}
            onQuery={setQuery}
            emptyText={rows.length ? NO_MATCH : (isScopeSuppressed(dashboardData) ? SUPPRESSED_EMPTY : undefined)}
        />
    </NetworkDashboardShell>
    );
};

export default PriorityActionsLearningProgramPage;
